import { alphaSort } from 'vet-bones/utils';

import {
  ProjectGeoDataByIri,
  ProjectGeoDataProperty,
} from 'src/constants/projectGeoData';
import { ModelConceptClass } from 'src/containers/ProjectDiagram/types/data';
import { ModelSnapshot } from 'src/containers/ProjectDiagram/types/model';
import {
  isModelConceptClass,
  isModelConceptHierarchy,
} from 'src/containers/ProjectDiagram/utils/data';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { GEO_DATA_KEY_BY_IRI } from 'src/templates/queries/geoData';
import {
  DESIGNER_COLOR_ID_IRI,
  GEOSPARQL_WKT_LITERAL_IRI,
  OWL_CLASS_IRI,
  RDFS_COMMENT_IRI,
  RDFS_LABEL_IRI,
  RDFS_SUB_CLASS_OF_IRI,
  XSD_DECIMAL_IRI,
} from 'src/templates/queries/iris';
import { escapeDoubleQuotes } from 'src/utils/escapeDoubleQuotes';
import { isIriInProjectModels } from 'src/utils/isIriInProjectModels';

const DATATYPE_BY_GEO_PROPERTY: Record<
  keyof ProjectGeoDataByIri,
  string | undefined
> = {
  loc: GEOSPARQL_WKT_LITERAL_IRI,
  lat: XSD_DECIMAL_IRI,
  long: XSD_DECIMAL_IRI,
  nodeSize: XSD_DECIMAL_IRI,
  popupTitle: undefined,
  popupContent: undefined,
  tooltipTitle: undefined,
  tooltipContent: undefined,
};

const modelConceptClassSort = alphaSort(
  (item: ModelConceptClass) => item.value
);

export const stringifyModelConceptClasses = ({
  filter,
  model,
  projectModelIds,
  targetModelId,
  includeMetadata = true,
}: {
  filter?: (conceptClass: ModelConceptClass) => boolean;
  model: ModelSnapshot;
  projectModelIds?: Set<string>;
  targetModelId: string;
  includeMetadata?: boolean;
}): string => {
  const hierarchyByIri = model.linkDataArray
    .filter(isModelConceptHierarchy)
    .reduce<Record<string, Set<string>>>((acc, data) => {
      const from = getModelConceptClassValue(data.from);
      const to = getModelConceptClassValue(data.to);

      if (projectModelIds) {
        const isFromInModels = isIriInProjectModels(
          from,
          projectModelIds,
          model.modelData.modelsByIri,
          targetModelId
        );
        const isToInModels = isIriInProjectModels(
          to,
          projectModelIds,
          model.modelData.modelsByIri,
          targetModelId
        );

        if (!isFromInModels && !isToInModels) {
          return acc;
        }
      }

      if (!acc[from]) {
        acc[from] = new Set([to]);
      } else {
        acc[from].add(to);
      }
      return acc;
    }, {});

  let classes = model.nodeDataArray
    .filter(isModelConceptClass)
    .filter(
      (classData) =>
        !projectModelIds ||
        isIriInProjectModels(
          classData.value,
          projectModelIds,
          model.modelData.modelsByIri,
          targetModelId
        )
    );

  if (filter) {
    classes = classes.filter(filter);
  }

  return classes
    .sort(modelConceptClassSort)
    .map(({ value }) => {
      const lines = [`<${value}> a <${OWL_CLASS_IRI}>`];

      const label = model.modelData.labelsByIri[value];
      if (label) {
        lines.push(`<${RDFS_LABEL_IRI}> "${escapeDoubleQuotes(label)}"`);
      }

      const description = model.modelData.descriptionsByIri[value];
      if (description) {
        lines.push(
          `<${RDFS_COMMENT_IRI}> """${escapeDoubleQuotes(description)}"""`
        );
      }

      const colorId = model.modelData.colorIdsByIri[value];
      if (includeMetadata && colorId) {
        lines.push(
          `<${DESIGNER_COLOR_ID_IRI}> "${escapeDoubleQuotes(colorId)}"`
        );
      }

      // Add geo data properties
      const geoData = model.modelData.geoDataByIri[value];
      if (includeMetadata && geoData) {
        const formatGeoValue = (
          property: keyof typeof geoData,
          geoProperty: ProjectGeoDataProperty
        ) => {
          if (!geoProperty) {
            return null;
          }

          // For attribute references, validate that the attribute still exists
          if (geoProperty.isAttribute) {
            if (!model.modelData.attributesByIri[geoProperty.value]) {
              return null; // Skip publishing if attribute doesn't exist
            }

            return `<${geoProperty.value}>`;
          }

          const datatypeIri = DATATYPE_BY_GEO_PROPERTY[property];
          return datatypeIri
            ? `"${escapeDoubleQuotes(geoProperty.value)}"^^<${datatypeIri}>`
            : `"${escapeDoubleQuotes(geoProperty.value)}"`;
        };

        const addGeoData = (
          property: keyof typeof geoData,
          propertyIri: string
        ) => {
          const value = geoData[property];
          if (value) {
            const formattedValue = formatGeoValue(
              property,
              value as ProjectGeoDataProperty
            );
            if (formattedValue) {
              lines.push(`<${propertyIri}> ${formattedValue}`);
            }
          }
        };

        Object.entries(GEO_DATA_KEY_BY_IRI).forEach(([iri, propertyKey]) => {
          addGeoData(propertyKey, iri);
        });
      }

      const hierarchy = Array.from(hierarchyByIri[value] || []);
      if (hierarchy.length) {
        const objects = hierarchy.map((object) => `<${object}>`).join(', ');
        lines.push(`<${RDFS_SUB_CLASS_OF_IRI}> ${objects}`);
      }

      return `${lines.join(' ;\n  ')} .`;
    })
    .join('\n');
};
